import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Typography,
  Grid,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Mic as MicIcon,
  ArrowForward as ArrowForwardIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import HoverCard3D from '../components/HoverCard3D';
import { useStartSession } from '../components/StartSessionGate';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationsContext';
import SuperAdminPlatformDashboard from './SuperAdminPlatformDashboard';
import OrgDashboard from './OrgDashboard';
import NodeOverview from './NodeOverview';
import UnfinishedVisits from '../components/UnfinishedVisits';
import ProviderQueueControl from '../components/ProviderQueueControl';
import UndeliveredMessages from '../components/UndeliveredMessages';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function orgDashboardSubtitle(orgType) {
  const t = (orgType || 'clinic').toLowerCase();
  return t === 'hospital' ? 'Hospital dashboard' : 'Clinic dashboard';
}

function EcgSvg({ sx }) {
  return (
    <Box
      component="svg"
      viewBox="0 0 240 100"
      sx={{ width: '100%', maxWidth: 280, ...sx }}
    >
      <defs>
        <linearGradient id="ecgGrad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#0d9488" />
          <stop offset="100%" stopColor="#5eead4" />
        </linearGradient>
      </defs>
      <path
        d="M0 50 L20 50 L28 50 L35 20 L42 75 L50 50 L240 50"
        fill="none"
        stroke="url(#ecgGrad)"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.9"
      />
      <path
        d="M0 50 L15 50 L22 50 L30 35 L38 65 L45 50 L240 50"
        fill="none"
        stroke="#134e4a"
        strokeWidth="1.5"
        opacity="0.25"
      />
      <circle cx="200" cy="50" r="14" fill="#0d9488" opacity="0.2" />
      <circle cx="200" cy="50" r="8" fill="#0d9488" />
    </Box>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { startSession, gateDialog } = useStartSession();
  const { user } = useAuth();
  const { assignmentVersion } = useNotifications();
  const isSuperAdmin = user?.role === 'super_admin';
  // An org admin's session names an organisation, not a clinic, so the clinic-scoped
  // fetches below would 403. They get the organisation rollup instead.
  const isOrgAdmin = user?.role === 'org_admin';
  // Enterprise and region admins are scoped to a LEVEL, not a clinic, so the
  // clinic dashboard below has nothing to load for them. They get the node screen.
  const isNodeAdmin = ['enterprise_admin', 'region_admin'].includes(user?.role);

  const practiceName = user?.practice_name?.trim() || '';
  const orgType = user?.org_type || 'clinic';
  const isStaff = user?.role === 'staff';
  const isClinicAdmin = user?.role === 'clinic_admin';
  const isProviderOnly = user?.role === 'provider';
  const canScribe = isProviderOnly || isClinicAdmin;
  const [recentPatients, setRecentPatients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // A level admin's session names no clinic, so the clinic-scoped fetch below can
    // only be refused — it was, on every visit to their home page, as a console
    // error nobody could act on.
    if (isSuperAdmin || isOrgAdmin || isNodeAdmin) {
      setLoading(false);
      return;
    }
    axios
      // "Patients on your list" means yours: only the assigned doctor starts their
      // sessions, so listing the whole clinic here offered Start buttons that would fail.
      // Staff have no list of their own; theirs stays "Recent patients" across the clinic.
      .get(`${API_URL}/api/v1/clinical/patients${isStaff ? '' : '?mine=true'}`)
      .then((res) => {
        const list = (res.data?.patients || []).slice(0, 5).map((p) => ({
          id: p.patient_id,
          name: p.name,
          reason: p.reason || 'Visit',
          status: 'scheduled',
        }));
        setRecentPatients(list);
      })
      .catch(() => setRecentPatients([]))
      .finally(() => setLoading(false));
  }, [isSuperAdmin, isOrgAdmin, isStaff, assignmentVersion, isNodeAdmin]);

  if (isSuperAdmin) {
    return <SuperAdminPlatformDashboard />;
  }

  if (isOrgAdmin) {
    return <OrgDashboard />;
  }

  if (isNodeAdmin) {
    return <NodeOverview />;
  }

  return (
    <Box>
      {gateDialog}
      {/* Anything left open sits above the welcome — it is the one thing on this
          screen that needs acting on, and it renders nothing when there is none. */}
      {/* Undelivered patient messages sit above everything: somebody may need to be
          phoned, and it renders nothing when there is nothing to report. Staff and
          clinic admins only — the endpoint refuses anyone else anyway. */}
      <UndeliveredMessages enabled={isClinicAdmin || isStaff} />
      {canScribe && <UnfinishedVisits />}
      {/* The clinician's own line: are they seeing patients, and who is next. Renders
          nothing for roles that hold no queue. */}
      {canScribe && <ProviderQueueControl />}
      <Grid container spacing={3} sx={{ mb: 3 }} alignItems="center">
        <Grid item xs={12} md={7}>
          <Typography
            variant="h4"
            sx={{
              fontFamily: '"Source Serif 4", Georgia, serif',
              fontWeight: 700,
              color: '#042f2e',
              mb: 1,
            }}
          >
            {practiceName ? `Welcome to ${practiceName}` : 'Welcome'}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontFamily: '"Outfit", sans-serif',
              fontWeight: 600,
              color: '#0f766e',
              mb: 1.5,
              fontSize: '1.1rem',
            }}
          >
            {orgDashboardSubtitle(orgType)}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75, maxWidth: 560 }}>
            {isStaff
              ? 'Register patients and update intake details from Manage Patients. Providers see only patients assigned to them.'
              : isProviderOnly
                ? 'Your patient list and quick actions are below. Open Insights for session counts and trends.'
                : 'Your patient list and quick actions are below. Open Insights for clinic-wide metrics and the doctor breakdown.'}
          </Typography>
        </Grid>
        <Grid item xs={12} md={5}>
          <Box
            sx={{
              p: 2.5,
              borderRadius: 3,
              background: 'linear-gradient(145deg, #ffffff 0%, #ecfdf5 100%)',
              border: '1px solid rgba(13, 148, 136, 0.15)',
              boxShadow: '0 16px 40px -18px rgba(4, 47, 46, 0.2)',
              transition: 'transform 0.45s cubic-bezier(0.22, 1, 0.36, 1)',
              '&:hover': {
                transform: 'perspective(1000px) rotateX(5deg) rotateY(-4deg)',
              },
            }}
          >
            <EcgSvg />
          </Box>
        </Grid>
      </Grid>

      {!isStaff && (
        <Box sx={{ mb: 3 }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/insights')}
          >
            Open Insights
          </Button>
        </Box>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={!isStaff ? 8 : 12}>
          <HoverCard3D
            cardSx={{
              background: '#ffffff',
              border: '1px solid rgba(4, 47, 46, 0.08)',
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={700} sx={{ fontFamily: '"Source Serif 4", Georgia, serif' }}>
                  {isStaff ? 'Recent patients' : 'Patients on your list'}
                </Typography>
                <Button size="small" endIcon={<ArrowForwardIcon />} onClick={() => navigate('/patients')}>
                  View all
                </Button>
              </Box>
              <List>
                {loading ? (
                  <ListItem sx={{ justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </ListItem>
                ) : recentPatients.length === 0 ? (
                  <ListItem>
                    <ListItemText
                      primary="No patients yet"
                      secondary="Add patients or open the list to start a scribe session."
                    />
                  </ListItem>
                ) : (
                  recentPatients.map((patient) => (
                    <ListItem
                      key={patient.id}
                      onClick={() => navigate('/patients', { state: { openPatientId: patient.id } })}
                      sx={{
                        bgcolor: patient.status === 'recording' ? 'primary.50' : 'grey.50',
                        borderRadius: 2,
                        mb: 1,
                        border: '1px solid rgba(4, 47, 46, 0.06)',
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: 'primary.50',
                          borderColor: 'primary.main',
                        },
                      }}
                      secondaryAction={
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
                          <Chip
                            size="small"
                            label={patient.status}
                            color={
                              patient.status === 'completed'
                                ? 'success'
                                : patient.status === 'recording'
                                  ? 'primary'
                                  : 'default'
                            }
                          />
                          {canScribe && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<MicIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                startSession(patient.id);
                              }}
                            >
                              {/* This used to read "Continue" when the visit was mid-
                                  recording, but the list above hardcodes status to
                                  'scheduled', so that branch was unreachable. Resuming
                                  is handled by <UnfinishedVisits /> at the top. */}
                              Start
                            </Button>
                          )}
                        </Box>
                      }
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'primary.main', fontWeight: 700 }}>{patient.name.charAt(0)}</Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={patient.name}
                        primaryTypographyProps={{ fontWeight: 700 }}
                        secondary={
                          <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CalendarIcon fontSize="inherit" />
                            {patient.reason}
                          </Box>
                        }
                      />
                    </ListItem>
                  ))
                )}
              </List>
            </CardContent>
          </HoverCard3D>
        </Grid>

        {!isStaff && (
          <Grid item xs={12} md={4}>
            <HoverCard3D
              cardSx={{
                background: 'linear-gradient(160deg, #0d9488 0%, #115e59 100%)',
                color: '#fff',
                border: 'none',
              }}
            >
              <CardContent>
                <Typography variant="h6" fontWeight={700} sx={{ fontFamily: '"Source Serif 4", Georgia, serif' }} gutterBottom>
                  Quick actions
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, mb: 2 }}>
                  {isClinicAdmin
                    ? 'Jump into documentation, triage pending reviews, or open the full patient list.'
                    : 'Jump into documentation or triage pending reviews.'}
                </Typography>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  startIcon={<MicIcon />}
                  sx={{
                    mt: 1,
                    bgcolor: '#ecfdf5',
                    color: '#042f2e',
                    fontWeight: 700,
                    py: 1.25,
                    '&:hover': { bgcolor: '#ccfbf1' },
                  }}
                  onClick={() => navigate('/patients')}
                >
                  Start new session
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  size="large"
                  sx={{ mt: 2, borderColor: 'rgba(255,255,255,0.5)', color: '#fff' }}
                  onClick={() => navigate('/patients')}
                >
                  {isClinicAdmin ? 'View patients & reviews' : 'Open patient list'}
                </Button>
              </CardContent>
            </HoverCard3D>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
